import React, { useState, useEffect } from 'react';
import {
  Shield,
  Key as KeyIcon,
  Bell as BellIcon,
  Globe as GlobeIcon,
  Database as DatabaseIcon,
  Mail as MailIcon,
  Smartphone as SmartphoneIcon,
  Lock as LockIcon,
  Save as SaveIcon,
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff,
  RefreshCw,
  BarChart,
  Settings as SettingsIcon,
  Activity,
  X,
  Server,
  Monitor,
  Users,
  Package,
  Mail,
  Bell,
  Database,
  Globe,
  Save
} from 'lucide-react';

import { GoogleAnalytics } from '../../lib/analytics';

import { supabase } from '../../lib/supabase';

const Settings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('general');
  
  const [settings, setSettings] = useState({
    // General Settings
    siteName: 'Made in BD',
    siteDescription: 'Leading B2B marketplace for Bangladeshi exports',
    maintenanceMode: false,
    allowRegistration: true,
    
    // Security Settings
    enableCaptcha: false,
    captchaOnLogin: false,
    captchaOnRegister: false,
    captchaOnPasswordReset: false,
    enableTwoFactor: false,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    
    // Email Settings
    emailProvider: 'zeptomail',
    smtpHost: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    fromEmail: 'noreply@madeinbd.com',
    fromName: 'Made in BD',
    
    // Notification Settings
    emailNotifications: true,
    orderNotifications: true,
    supplierNotifications: true,
    systemAlerts: true,
    
    // API Settings
    supabaseUrl: '',
    supabaseAnonKey: '',
    zeptomailApiUrl: '',
    zeptomailToken: '',
    
    // Captcha Settings
    captchaType: 'simple',
    recaptchaSiteKey: '',
    recaptchaSecretKey: '',
    recaptchaVersion: 'v2',
    recaptchaThreshold: '0.5',
    
    // Performance Settings
    enableCaching: true,
    cacheTimeout: 300,
    enableCompression: true,
    enableCdn: false
  });
  
  // Google Analytics management
  const [analyticsEnabled, setAnalyticsEnabled] = useState(GoogleAnalytics.isAnalyticsEnabled());
  const [trackingId, setTrackingId] = useState(GoogleAnalytics.getTrackingId());
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [newTrackingId, setNewTrackingId] = useState(trackingId);

  // Load settings from database
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        
        // Try to fetch settings from database
        const { data, error } = await supabase
          .from('admin_settings')
          .select('*');
        
        if (error) {
          console.warn('Could not load settings from database:', error);
          // Load from localStorage as fallback
          const savedSettings = localStorage.getItem('admin_settings');
          if (savedSettings) {
            const parsed = JSON.parse(savedSettings);
            setSettings(prev => ({ ...prev, ...parsed }));
          }
        } else if (data && data.length > 0) {
          // Merge database settings with default settings
          const dbSettings = data.reduce((acc, setting) => {
            acc[setting.setting_type] = setting.settings;
            return acc;
          }, {} as any);
          
          // Flatten the settings object
          const flatSettings = Object.values(dbSettings).reduce((acc, settingGroup: any) => {
            return { ...acc, ...settingGroup };
          }, {});
          
          setSettings(prev => ({ ...prev, ...flatSettings }));
        }
      } catch (err) {
        console.error('Error loading settings:', err);
        setError('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    
    loadSettings();
  }, []);
  
  // Load analytics preferences on component mount
  useEffect(() => {
    const savedEnabled = localStorage.getItem('analytics_enabled');
    const savedTrackingId = localStorage.getItem('ga_tracking_id');
    
    if (savedEnabled !== null) {
      const enabled = savedEnabled === 'true';
      setAnalyticsEnabled(enabled);
      GoogleAnalytics.setEnabled(enabled);
    }
    
    if (savedTrackingId) {
      setTrackingId(savedTrackingId);
      setNewTrackingId(savedTrackingId);
      GoogleAnalytics.updateTrackingId(savedTrackingId);
    }
  }, []);

  // Save settings to database and localStorage
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Save to localStorage immediately for instant effect
      localStorage.setItem('admin_settings', JSON.stringify(settings));
      
      // Try to save to database
      try {
        // Group settings by type
        const settingGroups = {
          general: {
            siteName: settings.siteName,
            siteDescription: settings.siteDescription,
            maintenanceMode: settings.maintenanceMode,
            allowRegistration: settings.allowRegistration
          },
          security: {
            enableCaptcha: settings.enableCaptcha,
            captchaOnLogin: settings.captchaOnLogin,
            captchaOnRegister: settings.captchaOnRegister,
            captchaOnPasswordReset: settings.captchaOnPasswordReset,
            enableTwoFactor: settings.enableTwoFactor,
            sessionTimeout: settings.sessionTimeout,
            maxLoginAttempts: settings.maxLoginAttempts
          },
          email: {
            emailProvider: settings.emailProvider,
            smtpHost: settings.smtpHost,
            smtpPort: settings.smtpPort,
            smtpUsername: settings.smtpUsername,
            smtpPassword: settings.smtpPassword,
            fromEmail: settings.fromEmail,
            fromName: settings.fromName
          },
          notifications: {
            emailNotifications: settings.emailNotifications,
            orderNotifications: settings.orderNotifications,
            supplierNotifications: settings.supplierNotifications,
            systemAlerts: settings.systemAlerts
          },
          api: {
            supabaseUrl: settings.supabaseUrl,
            supabaseAnonKey: settings.supabaseAnonKey,
            zeptomailApiUrl: settings.zeptomailApiUrl,
            zeptomailToken: settings.zeptomailToken
          },
          captcha: {
            captchaType: settings.captchaType,
            recaptchaSiteKey: settings.recaptchaSiteKey,
            recaptchaSecretKey: settings.recaptchaSecretKey,
            recaptchaVersion: settings.recaptchaVersion,
            recaptchaThreshold: settings.recaptchaThreshold
          },
          performance: {
            enableCaching: settings.enableCaching,
            cacheTimeout: settings.cacheTimeout,
            enableCompression: settings.enableCompression,
            enableCdn: settings.enableCdn
          }
        };
        
        // Upsert each setting group
        for (const [settingType, settingData] of Object.entries(settingGroups)) {
          await supabase
            .from('admin_settings')
            .upsert({
              setting_type: settingType,
              settings: settingData
            }, {
              onConflict: 'setting_type'
            });
        }
        
        setSuccess('Settings saved successfully');
      } catch (dbError) {
        console.warn('Could not save to database, saved to localStorage only:', dbError);
        setSuccess('Settings saved locally (database unavailable)');
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Send test email function
  const sendTestEmail = async () => {
    try {
      setTestEmailLoading(true);
      setTestEmailResult(null);
      
      // Check if ZeptoMail is configured
      if (!settings.zeptomailApiUrl || !settings.zeptomailToken) {
        setTestEmailResult({
          success: false,
          message: 'ZeptoMail API URL and Token are required'
        });
        return;
      }
      
      // Prepare test email payload
      const payload = {
        from: {
          address: settings.fromEmail || 'noreply@madeinbd.com',
          name: settings.fromName || 'Made in BD'
        },
        to: [
          {
            email_address: {
              address: 'admin@madeinbd.com' // Test email address
            }
          }
        ],
        subject: 'Test Email from Made in BD Admin Panel',
        htmlbody: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <div style="background: linear-gradient(135deg, #f97316 0%, #dc2626 100%); padding: 20px; text-align: center; color: white;">
              <h1>Test Email</h1>
            </div>
            <div style="padding: 20px; border: 1px solid #e5e7eb; border-top: none;">
              <p>Hello Admin,</p>
              <p>This is a test email sent from the Made in BD admin panel to verify your ZeptoMail configuration.</p>
              <p>If you received this email, your email settings are working correctly!</p>
              <p>Test sent at: ${new Date().toLocaleString()}</p>
              <p>Best regards,<br>Made in BD System</p>
            </div>
          </div>
        `,
        textbody: `
          Test Email from Made in BD Admin Panel
          
          Hello Admin,
          
          This is a test email sent from the Made in BD admin panel to verify your ZeptoMail configuration.
          
          If you received this email, your email settings are working correctly!
          
          Test sent at: ${new Date().toLocaleString()}
          
          Best regards,
          Made in BD System
        `
      };
      
      // Send test email using ZeptoMail API
      const response = await fetch(settings.zeptomailApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Zoho-enczapikey ${settings.zeptomailToken}`
        },
        body: JSON.stringify(payload)
      });
      
      const responseData = await response.json();
      
      if (response.ok) {
        setTestEmailResult({
          success: true,
          message: 'Test email sent successfully! Check your inbox at admin@madeinbd.com'
        });
      } else {
        setTestEmailResult({
          success: false,
          message: `Failed to send test email: ${responseData.message || 'Unknown error'}`
        });
      }
      
    } catch (err) {
      console.error('Error sending test email:', err);
      setTestEmailResult({
        success: false,
        message: `Error sending test email: ${err instanceof Error ? err.message : 'Unknown error'}`
      });
    } finally {
      setTestEmailLoading(false);
      
      // Clear test result after 5 seconds
      setTimeout(() => {
        setTestEmailResult(null);
      }, 5000);
    }
  };

  const handleInputChange = (key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleToggleAnalytics = () => {
    const newState = !analyticsEnabled;
    setAnalyticsEnabled(newState);
    GoogleAnalytics.setEnabled(newState);
    
    // Store preference in localStorage
    localStorage.setItem('analytics_enabled', newState.toString());
  };

  const handleUpdateTrackingId = () => {
    if (newTrackingId && newTrackingId !== trackingId) {
      GoogleAnalytics.updateTrackingId(newTrackingId);
      setTrackingId(newTrackingId);
      localStorage.setItem('ga_tracking_id', newTrackingId);
      setShowAnalyticsModal(false);
      
      // Show success message
      alert('Google Analytics tracking ID updated successfully!');
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: SettingsIcon },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'analytics', label: 'Analytics', icon: BarChart },
    { id: 'api', label: 'API', icon: Database },
    { id: 'performance', label: 'Performance', icon: Server }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
              <p className="text-gray-600">Manage application settings and configuration</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Reset</span>
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Save Settings</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
            <CheckCircle className="h-5 w-5 mr-2" />
            <span>{success}</span>
          </div>
        )}
        
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-orange-100 text-orange-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className={`mr-3 h-5 w-5 ${
                    activeTab === tab.id ? 'text-orange-500' : 'text-gray-400'
                  }`} />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Settings Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm p-6">
              {/* General Settings */}
              {activeTab === 'general' && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">General Settings</h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Site Name
                      </label>
                      <input
                        type="text"
                        value={settings.siteName}
                        onChange={(e) => handleInputChange('siteName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Site Description
                      </label>
                      <textarea
                        value={settings.siteDescription}
                        onChange={(e) => handleInputChange('siteDescription', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Maintenance Mode</label>
                        <p className="text-sm text-gray-500">Temporarily disable the site for maintenance</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.maintenanceMode}
                          onChange={(e) => handleInputChange('maintenanceMode', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      </label>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Allow Registration</label>
                        <p className="text-sm text-gray-500">Allow new users to register accounts</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.allowRegistration}
                          onChange={(e) => handleInputChange('allowRegistration', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Settings */}
              {activeTab === 'security' && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">Security Settings</h2>
                  <div className="space-y-6">
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Captcha Settings</h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Captcha Type
                          </label>
                          <select
                            value={settings.captchaType || 'simple'}
                            onChange={(e) => handleInputChange('captchaType', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          >
                            <option value="simple">Simple Text Captcha</option>
                            <option value="recaptcha">Google reCAPTCHA</option>
                          </select>
                        </div>
                        
                        {settings.captchaType === 'recaptcha' && (
                          <div className="space-y-4 bg-blue-50 p-4 rounded-lg">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                reCAPTCHA Site Key
                              </label>
                              <input
                                type="text"
                                value={settings.recaptchaSiteKey || ''}
                                onChange={(e) => handleInputChange('recaptchaSiteKey', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                placeholder="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Get your site key from <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">Google reCAPTCHA Admin Console</a>
                              </p>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                reCAPTCHA Secret Key
                              </label>
                              <input
                                type="password"
                                value={settings.recaptchaSecretKey || ''}
                                onChange={(e) => handleInputChange('recaptchaSecretKey', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                placeholder="6LeIxAcTAAAAAGG-vFI1TnRWxMZNFuojJ4WifJWe"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Secret key for server-side verification
                              </p>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                reCAPTCHA Version
                              </label>
                              <select
                                value={settings.recaptchaVersion || 'v2'}
                                onChange={(e) => handleInputChange('recaptchaVersion', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                              >
                                <option value="v2">reCAPTCHA v2 (I'm not a robot)</option>
                                <option value="v3">reCAPTCHA v3 (Invisible)</option>
                              </select>
                            </div>
                            
                            {settings.recaptchaVersion === 'v3' && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Score Threshold (0.0 - 1.0)
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  max="1"
                                  step="0.1"
                                  value={settings.recaptchaThreshold || '0.5'}
                                  onChange={(e) => handleInputChange('recaptchaThreshold', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                  Lower scores indicate more likely bots. Recommended: 0.5
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Enable Captcha</label>
                            <p className="text-sm text-gray-500">Master switch for all captcha functionality</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings.enableCaptcha}
                              onChange={(e) => handleInputChange('enableCaptcha', e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                          </label>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Captcha on Login</label>
                            <p className="text-sm text-gray-500">Show captcha on login forms</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings.captchaOnLogin}
                              onChange={(e) => handleInputChange('captchaOnLogin', e.target.checked)}
                              disabled={!settings.enableCaptcha}
                              className="sr-only peer"
                            />
                            <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600 ${!settings.enableCaptcha ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                          </label>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Captcha on Registration</label>
                            <p className="text-sm text-gray-500">Show captcha on registration forms</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings.captchaOnRegister}
                              onChange={(e) => handleInputChange('captchaOnRegister', e.target.checked)}
                              disabled={!settings.enableCaptcha}
                              className="sr-only peer"
                            />
                            <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600 ${!settings.enableCaptcha ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                          </label>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Captcha on Password Reset</label>
                            <p className="text-sm text-gray-500">Show captcha on password reset forms</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings.captchaOnPasswordReset}
                              onChange={(e) => handleInputChange('captchaOnPasswordReset', e.target.checked)}
                              disabled={!settings.enableCaptcha}
                              className="sr-only peer"
                            />
                            <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600 ${!settings.enableCaptcha ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Authentication Security</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Two-Factor Authentication</label>
                            <p className="text-sm text-gray-500">Require 2FA for admin accounts</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings.enableTwoFactor}
                              onChange={(e) => handleInputChange('enableTwoFactor', e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                          </label>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Session Timeout (minutes)
                          </label>
                          <input
                            type="number"
                            min="5"
                            max="1440"
                            value={settings.sessionTimeout}
                            onChange={(e) => handleInputChange('sessionTimeout', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Max Login Attempts
                          </label>
                          <input
                            type="number"
                            min="3"
                            max="10"
                            value={settings.maxLoginAttempts}
                            onChange={(e) => handleInputChange('maxLoginAttempts', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Email Settings */}
              {activeTab === 'email' && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">Email Settings</h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Provider
                      </label>
                      <select
                        value={settings.emailProvider}
                        onChange={(e) => handleInputChange('emailProvider', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        <option value="zeptomail">ZeptoMail</option>
                        <option value="smtp">SMTP</option>
                        <option value="sendgrid">SendGrid</option>
                      </select>
                    </div>
                    
                    {settings.emailProvider === 'smtp' && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              SMTP Host
                            </label>
                            <input
                              type="text"
                              value={settings.smtpHost}
                              onChange={(e) => handleInputChange('smtpHost', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              SMTP Port
                            </label>
                            <input
                              type="number"
                              value={settings.smtpPort}
                              onChange={(e) => handleInputChange('smtpPort', parseInt(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              SMTP Username
                            </label>
                            <input
                              type="text"
                              value={settings.smtpUsername}
                              onChange={(e) => handleInputChange('smtpUsername', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              SMTP Password
                            </label>
                            <input
                              type="password"
                              value={settings.smtpPassword}
                              onChange={(e) => handleInputChange('smtpPassword', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                      </>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          From Email
                        </label>
                        <input
                          type="email"
                          value={settings.fromEmail}
                          onChange={(e) => handleInputChange('fromEmail', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          From Name
                        </label>
                        <input
                          type="text"
                          value={settings.fromName}
                          onChange={(e) => handleInputChange('fromName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Notification Settings */}
              {activeTab === 'notifications' && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">Notification Settings</h2>
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Email Notifications</label>
                        <p className="text-sm text-gray-500">Send email notifications to users</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.emailNotifications}
                          onChange={(e) => handleInputChange('emailNotifications', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      </label>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Order Notifications</label>
                        <p className="text-sm text-gray-500">Notify about new orders and status changes</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.orderNotifications}
                          onChange={(e) => handleInputChange('orderNotifications', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      </label>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Supplier Notifications</label>
                        <p className="text-sm text-gray-500">Notify about supplier registrations and updates</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.supplierNotifications}
                          onChange={(e) => handleInputChange('supplierNotifications', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      </label>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">System Alerts</label>
                        <p className="text-sm text-gray-500">Notify about system errors and issues</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.systemAlerts}
                          onChange={(e) => handleInputChange('systemAlerts', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Analytics Settings */}
              {activeTab === 'analytics' && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">Analytics Settings</h2>
                  <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <BarChart className="h-6 w-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">Google Analytics</h3>
                            <p className="text-sm text-gray-600">Manage website analytics and tracking</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">Analytics:</span>
                            <button
                              onClick={handleToggleAnalytics}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                analyticsEnabled ? 'bg-green-600' : 'bg-gray-200'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  analyticsEnabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                            <span className={`text-sm font-medium ${analyticsEnabled ? 'text-green-600' : 'text-gray-500'}`}>
                              {analyticsEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                          <button
                            onClick={() => setShowAnalyticsModal(true)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center space-x-2"
                          >
                            <Settings className="h-4 w-4" />
                            <span>Configure</span>
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center space-x-2 mb-2">
                            <Activity className="h-5 w-5 text-blue-600" />
                            <span className="font-medium text-gray-900">Current Status</span>
                          </div>
                          <p className="text-sm text-gray-600">
                            Google Analytics is currently <span className={`font-medium ${analyticsEnabled ? 'text-green-600' : 'text-red-600'}`}>
                              {analyticsEnabled ? 'active' : 'inactive'}
                            </span> on all pages
                          </p>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center space-x-2 mb-2">
                            <BarChart className="h-5 w-5 text-blue-600" />
                            <span className="font-medium text-gray-900">Tracking ID</span>
                          </div>
                          <p className="text-sm text-gray-600 font-mono">{trackingId}</p>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center space-x-2 mb-2">
                            <Globe className="h-5 w-5 text-blue-600" />
                            <span className="font-medium text-gray-900">Coverage</span>
                          </div>
                          <p className="text-sm text-gray-600">All pages automatically tracked</p>
                        </div>
                      </div>
                      
                      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                        <h4 className="text-sm font-medium text-blue-800 mb-2">Analytics Features</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                          <li>• Automatic page view tracking on all routes</li>
                          <li>• User interaction and event tracking</li>
                          <li>• E-commerce and conversion tracking</li>
                          <li>• Real-time visitor monitoring</li>
                          <li>• Custom business event tracking</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* API Settings */}
              {activeTab === 'api' && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">API Settings</h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Supabase Configuration</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Supabase URL
                          </label>
                          <input
                            type="url"
                            value={settings.supabaseUrl}
                            onChange={(e) => handleInputChange('supabaseUrl', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="https://your-project.supabase.co"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Supabase Anon Key
                          </label>
                          <input
                            type="password"
                            value={settings.supabaseAnonKey}
                            onChange={(e) => handleInputChange('supabaseAnonKey', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">ZeptoMail Configuration</h3>
                     <div className="flex items-center justify-between mb-4">
                       <span className="text-sm text-gray-600">Test your email configuration</span>
                       <button
                         type="button"
                         onClick={sendTestEmail}
                         disabled={testEmailLoading || !settings.zeptomailApiUrl || !settings.zeptomailToken}
                         className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                       >
                         {testEmailLoading ? (
                           <>
                             <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                             <span>Sending...</span>
                           </>
                         ) : (
                           <>
                             <Mail className="h-4 w-4" />
                             <span>Send Test Email</span>
                           </>
                         )}
                       </button>
                     </div>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            ZeptoMail API URL
                          </label>
                          <input
                            type="url"
                            value={settings.zeptomailApiUrl}
                            onChange={(e) => handleInputChange('zeptomailApiUrl', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="https://api.zeptomail.com/v1.1/email"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            ZeptoMail Token
                          </label>
                          <input
                            type="password"
                            value={settings.zeptomailToken}
                            onChange={(e) => handleInputChange('zeptomailToken', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Test Email Result */}
                  {testEmailResult && (
                    <div className={`mt-4 p-4 rounded-lg ${
                      testEmailResult.success 
                        ? 'bg-green-50 border border-green-200 text-green-700' 
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                      <div className="flex items-center">
                        {testEmailResult.success ? (
                          <CheckCircle className="h-5 w-5 mr-2" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 mr-2" />
                        )}
                        <span>{testEmailResult.message}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Performance Settings */}
              {activeTab === 'performance' && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">Performance Settings</h2>
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Enable Caching</label>
                        <p className="text-sm text-gray-500">Cache API responses for better performance</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.enableCaching}
                          onChange={(e) => handleInputChange('enableCaching', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      </label>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cache Timeout (seconds)
                      </label>
                      <input
                        type="number"
                        min="60"
                        max="3600"
                        value={settings.cacheTimeout}
                        onChange={(e) => handleInputChange('cacheTimeout', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Enable Compression</label>
                        <p className="text-sm text-gray-500">Compress responses to reduce bandwidth</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.enableCompression}
                          onChange={(e) => handleInputChange('enableCompression', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      </label>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Enable CDN</label>
                        <p className="text-sm text-gray-500">Use CDN for static assets</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.enableCdn}
                          onChange={(e) => handleInputChange('enableCdn', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    
      {/* Google Analytics Configuration Modal */}
      {showAnalyticsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Configure Google Analytics</h3>
                <button
                  onClick={() => setShowAnalyticsModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="trackingId" className="block text-sm font-medium text-gray-700 mb-2">
                    Google Analytics Tracking ID
                  </label>
                  <input
                    id="trackingId"
                    type="text"
                    value={newTrackingId}
                    onChange={(e) => setNewTrackingId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    placeholder="G-XXXXXXXXXX"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter your Google Analytics 4 measurement ID (starts with G-)
                  </p>
                </div>
                
                <div className="flex items-center">
                  <input
                    id="enableAnalytics"
                    type="checkbox"
                    checked={analyticsEnabled}
                    onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="enableAnalytics" className="ml-2 block text-sm text-gray-900">
                    Enable Google Analytics tracking
                  </label>
                </div>
                
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    <strong>Note:</strong> Changes will apply to all pages automatically. 
                    The tracking code is already embedded in the site header.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAnalyticsModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateTrackingId}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;